/**
 * Utility functions for handling referral codes from URLs
 */

const REFERRAL_CODE_STORAGE_KEY = 'pendingReferralCode';

/** Minimum length for a valid referral code from URL */
const MIN_REFERRAL_CODE_LENGTH = 6;

/**
 * Extracts referral code from URL path.
 * Only the dedicated /ref/CODE format is treated as referral (e.g. /ref/VISHKUMA9821).
 * Other paths (e.g. /something, /consultant-home) are never treated as referral so
 * the referral dialog only shows real referral codes after signup.
 * @param pathname - The current pathname from the URL
 * @returns The referral code if found, null otherwise
 */
export const extractReferralCodeFromPath = (pathname: string): string | null => {
  const path = (pathname.startsWith('/') ? pathname.slice(1) : pathname).trim();
  if (!path) return null;

  // Only accept referral when URL is explicitly /ref/CODE (referral link format)
  const refPrefix = 'ref/';
  if (!path.toLowerCase().startsWith(refPrefix)) {
    return null;
  }
  const code = path.slice(refPrefix.length).split('/')[0].trim();
  if (!code) return null;

  // Validate code format: alphanumeric, hyphens, underscores; minimum length
  const referralCodePattern = /^[A-Z0-9_-]+$/i;
  if (referralCodePattern.test(code) && code.length >= MIN_REFERRAL_CODE_LENGTH) {
    return code.toUpperCase();
  }
  return null;
};

/** True for dedicated referral landing URLs (e.g. /ref/VISHKUMA6691). */
export const isReferralLandingPath = (pathname: string): boolean => {
  return extractReferralCodeFromPath(pathname) !== null;
};

/**
 * Stores referral code in localStorage
 * @param code - The referral code to store
 */
export const storeReferralCode = (code: string): void => {
  try {
    localStorage.setItem(REFERRAL_CODE_STORAGE_KEY, code);
  } catch (error) {
    console.error('Error storing referral code:', error);
  }
};

/** Route segments / path values that must never be treated as referral codes (e.g. BOOKING, consultant-home). */
const KNOWN_NON_REFERRAL_VALUES = new Set([
  'CONSULTANT-HOME', 'CLIENT-HOME', 'CAREER-HOME', 'MARKETING-HOME', 'IT-HOME',
  'BLOGS',
  'BOOKING', 'CONSULTATION', 'DASHBOARD', 'MESSAGES', 'SETTINGS', 'PROFILE',
  'AVAILABILITY', 'FAVORITES', 'REFERRAL', 'SEARCH', 'HOME', 'AUTH', 'LOGIN',
  'REGISTER', 'PERSONAL-INFO', 'PROFESSIONAL-INFO', 'PORTFOLIO',
]);

/**
 * Returns true if the value is a known route segment (e.g. CONSULTANT-HOME), not a real referral code.
 */
export const isKnownRouteSegment = (code: string | null | undefined): boolean => {
  if (!code || typeof code !== 'string') return false;
  return KNOWN_NON_REFERRAL_VALUES.has(code.trim().toUpperCase());
};

/** Pattern for valid referral code (only these are shown in referral dialog) */
const VALID_REFERRAL_CODE_PATTERN = /^[A-Z0-9_-]+$/i;

/**
 * Returns true if the value looks like a real referral code (format + length).
 * Used so only referral codes from /ref/CODE flow are used, not random URL content.
 */
const isValidReferralCodeFormat = (code: string | null | undefined): boolean => {
  if (!code || typeof code !== 'string') return false;
  const trimmed = code.trim();
  return trimmed.length >= MIN_REFERRAL_CODE_LENGTH && VALID_REFERRAL_CODE_PATTERN.test(trimmed);
};

/**
 * Retrieves stored referral code from localStorage.
 * Returns null if the stored value is a known route segment or not a valid referral format.
 */
export const getStoredReferralCode = (): string | null => {
  try {
    const stored = localStorage.getItem(REFERRAL_CODE_STORAGE_KEY);
    if (!stored || isKnownRouteSegment(stored) || !isValidReferralCodeFormat(stored)) {
      if (stored && (isKnownRouteSegment(stored) || !isValidReferralCodeFormat(stored))) {
        localStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);
      }
      return null;
    }
    return stored;
  } catch (error) {
    console.error('Error retrieving referral code:', error);
    return null;
  }
};

/**
 * Clears stored referral code from localStorage
 */
export const clearStoredReferralCode = (): void => {
  try {
    localStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing referral code:', error);
  }
};

/**
 * Checks if there's a referral code in the current URL or stored
 * @param pathname - The current pathname from the URL
 * @returns The referral code if found (from URL or storage), null otherwise
 */
export const getReferralCode = (pathname: string): string | null => {
  // First check URL
  const urlCode = extractReferralCodeFromPath(pathname);
  if (urlCode) {
    // Store it for later use
    storeReferralCode(urlCode);
    return urlCode;
  }
  
  // If no code in URL, check storage
  return getStoredReferralCode();
};

/**
 * Checks if there's a referral code available (in URL or stored)
 * @param pathname - The current pathname from the URL
 * @returns true if a referral code exists, false otherwise
 */
export const hasReferralCode = (pathname: string): boolean => {
  return getReferralCode(pathname) !== null;
};

/** Pending referral from session or localStorage (signup / ref link flow). */
export const getPendingReferralCode = (): string | null => {
  try {
    const fromSession = sessionStorage.getItem('pendingReferralCodeForProfile');
    const fromStorage = getStoredReferralCode();
    const raw = (fromSession?.trim() || fromStorage?.trim() || '');
    if (!raw || isKnownRouteSegment(raw) || !isValidReferralCodeFormat(raw)) {
      return null;
    }
    return raw.toUpperCase();
  } catch {
    return null;
  }
};

